import { isCurrentExperience } from '@/lib/experiences';
import { addDays, format, nextMonday } from 'date-fns';
import { dialablePhone } from '@/lib/format-phone';
import type {
  EngagePhone,
  EngageTask,
  EngageTaskPriority,
} from '@/store/features/engage.feature';
import type { ShareableUser } from '@/types';
import { taskBlocked } from './task-blocked';
import { signalTier } from './task-signal.util';

/** The small bordered pill the grid and the cards use for a state. */
/**
 * The pill tone of a call's outcome: a meeting or a conversation reads green,
 * a refusal or a bad number red, a miss (no answer, a voicemail) amber, and
 * any outcome the workspace made up is neutral.
 */
export function outcomeTone(outcome: string | null | undefined): string {
  const value = (outcome ?? '').toLowerCase();
  if (
    /not interested|wrong|bad number|do not|unqualified|disqualif|declin|reject|invalid|unsubscribe/.test(
      value
    )
  )
    return FIT_TONE.disqualified;
  if (
    /meeting|booked|interested|connected|conversation|positive|qualified/.test(
      value
    )
  )
    return FIT_TONE.qualified;
  if (
    /no answer|voicemail|busy|left|try again|call back|callback|gatekeeper/.test(
      value
    )
  )
    return FIT_TONE.needs_review;
  return 'border-border bg-muted/40 text-muted-foreground';
}

export const PILL =
  'inline-flex h-6 max-w-full items-center gap-1 truncate rounded-md border px-2 text-[11px] font-medium';

/**
 * The line under a lead's name: their title and company. A title that already
 * names the company ("CEO @ Airbnb") stands alone, so the company is not said
 * twice.
 */
export function leadSubtitle(task: {
  title?: string | null;
  company?: string | null;
  email?: string | null;
}): string {
  const title = task.title?.trim() ?? '';
  const company = task.company?.trim() ?? '';
  if (!title) return company || task.email || '';
  if (!company || title.toLowerCase().includes(company.toLowerCase()))
    return title;
  return `${title} at ${company}`;
}

/** Pill tones by the Lead scoring verdict. */
export const FIT_TONE: Record<string, string> = {
  qualified:
    'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/25 dark:bg-emerald-500/10 dark:text-emerald-300',
  disqualified:
    'border-red-200 bg-red-50 text-red-700 dark:border-red-500/25 dark:bg-red-500/10 dark:text-red-300',
  needs_review:
    'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/25 dark:bg-amber-500/10 dark:text-amber-300',
};

/** A pill with no verdict in it: a plain fact such as a call's outcome. */
export const NEUTRAL_TONE =
  'border-border bg-muted/40 text-foreground dark:bg-white/[0.04]';

/** The one label the task forms use for a field's name. */
export const FIELD_LABEL = 'text-[12px] font-medium text-muted-foreground';

/** Today as YYYY-MM-DD in the viewer's own timezone. */
export function todayLocal(now: Date = new Date()): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function dateOffset(days: number, from: string): string {
  const date = new Date(`${from}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/**
 * The days a rep jumps to from a follow-up or a snooze: one set, so the two
 * menus never offer different choices. Snooze takes the ones after today.
 */
export const DAY_PRESETS: readonly (readonly [label: string, days: number])[] =
  [
    ['Today', 0],
    ['Tomorrow', 1],
    ['In 2 days', 2],
    ['Next week', 7],
  ];

export type DueFilter =
  | 'due_now'
  | 'today'
  | 'overdue'
  | 'tomorrow'
  | 'next7'
  | 'all'
  | 'custom';

export const DUE_OPTIONS: readonly (readonly [DueFilter, string])[] = [
  ['due_now', 'Today and overdue'],
  ['today', 'Today'],
  ['overdue', 'Overdue'],
  ['tomorrow', 'Tomorrow'],
  ['next7', 'Next 7 days'],
  ['all', 'Any due date'],
  ['custom', 'Custom range'],
];

/**
 * Whether a task falls in a due filter. A task with no due date is due now: it
 * is open work, and hiding it from the default view would bury it.
 */
export function matchesDue(
  due: string | null,
  filter: DueFilter,
  today: string,
  range?: { from: string; to: string }
): boolean {
  switch (filter) {
    case 'due_now':
      return !due || due <= today;
    case 'today':
      return !due || due === today;
    case 'overdue':
      return Boolean(due && due < today);
    case 'tomorrow':
      return due === dateOffset(1, today);
    case 'next7':
      return !due || (due >= today && due <= dateOffset(6, today));
    case 'custom':
      return Boolean(
        due &&
        range?.from &&
        range.to &&
        range.from <= range.to &&
        due >= range.from &&
        due <= range.to
      );
    default:
      return true;
  }
}

export function isOverdue(task: EngageTask, today: string): boolean {
  return (
    task.status !== 'completed' &&
    Boolean(task.due_date) &&
    task.due_date! < today
  );
}

export function dueLabel(task: EngageTask, today: string): string {
  if (!task.due_date) return 'Today';
  const day =
    task.due_date === today
      ? 'Today'
      : task.due_date === dateOffset(1, today)
        ? 'Tomorrow'
        : task.due_date === dateOffset(-1, today)
          ? 'Yesterday'
          : new Date(`${task.due_date}T12:00:00Z`).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              timeZone: 'UTC',
            });
  if (!task.time24) return day;
  const [hour, minute] = task.time24.split(':').map(Number);
  return `${day}, ${hour % 12 || 12}:${String(minute).padStart(2, '0')} ${hour < 12 ? 'AM' : 'PM'}`;
}

/** Earliest (or latest) due first; undated tasks count as due today. */
const PRIORITY_RANK: Record<string, number> = { High: 0, Medium: 1, Low: 2 };

/** By due day; within a day the urgent ones first, then by time. */
export function sortTasks(
  tasks: EngageTask[],
  sort: 'due_asc' | 'due_desc',
  today: string
): EngageTask[] {
  const direction = sort === 'due_desc' ? -1 : 1;
  return [...tasks].sort(
    (a, b) =>
      direction * (a.due_date ?? today).localeCompare(b.due_date ?? today) ||
      (PRIORITY_RANK[a.priority] ?? 1) - (PRIORITY_RANK[b.priority] ?? 1) ||
      direction * (a.time24 ?? '00:00').localeCompare(b.time24 ?? '00:00') ||
      a.id.localeCompare(b.id)
  );
}

/**
 * Highest lead score first; tasks with no score last. Tasks with the same
 * score keep the order they came in (the server's rank, or the sort chosen),
 * so ranking by score refines the queue instead of shuffling it.
 */
export function rankByLeadScore(tasks: EngageTask[]): EngageTask[] {
  return tasks
    .map((task, index) => ({ task, index }))
    .sort((a, b) => {
      const left = a.task.fit?.score;
      const right = b.task.fit?.score;
      if (left == null && right == null) return a.index - b.index;
      if (left == null) return 1;
      if (right == null) return -1;
      return right - left || a.index - b.index;
    })
    .map(({ task }) => task);
}

/** How "Call" opens the number. `tel:` works with any phone app or softphone;
 * `cleanlist` places the call in the browser (the native dialer), offered only
 * to a rep who can call from Cleanlist. */
export const DIAL_METHODS = [
  { key: 'cleanlist', label: 'Cleanlist' },
  { key: 'tel', label: 'Phone app' },
  { key: 'zoom', label: 'Zoom Phone' },
  { key: 'ringcentral', label: 'RingCentral' },
  { key: 'dialpad', label: 'Dialpad' },
] as const;
export type DialMethod = (typeof DIAL_METHODS)[number]['key'];

/** Trellus dials from its own app, so the dock links there when it's connected. */
export const TRELLUS_APP_URL = 'https://app.trellus.ai/';

export function dialHref(number: string, method: DialMethod): string {
  const target = dialablePhone(number);
  switch (method) {
    case 'zoom':
      return 'zoomphonecall://' + target;
    case 'ringcentral':
      return 'rcmobile://call?number=' + encodeURIComponent(target);
    case 'dialpad':
      return 'dialpad://' + target;
    default:
      return 'tel:' + target;
  }
}

/** 'mobile' reads as 'Mobile'; an unknown type shows no label at all. */
export function phoneTypeLabel(phone: EngagePhone): string {
  const type = phone.type?.trim().toLowerCase();
  if (!type || type === 'unknown') return '';
  return type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ');
}

/** 95 seconds reads as '1:35'. */
export function formatDuration(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds));
  return Math.floor(safe / 60) + ':' + String(safe % 60).padStart(2, '0');
}

/** Where a task came from: a step of the sequence, a reply, or the rep. */
export function taskSource(task: {
  origin?: string;
  type: string;
  step_number: number | null;
}): string {
  if (task.origin === 'follow_up') return 'Follow-up';
  if (task.type === 'Reply') return 'Reply';
  return task.step_number ? 'Step ' + task.step_number : '';
}

export type TaskView =
  | 'mine'
  | 'calls'
  | 'replies'
  | 'review'
  | 'team'
  | 'completed';

export type TaskFilterKey =
  | 'view'
  | 'due'
  | 'sequence'
  | 'owner'
  | 'priority'
  | 'type'
  | 'state'
  | 'outcome'
  | 'query'
  | 'from'
  | 'to';

const SELECTION_CLEARING: ReadonlySet<TaskFilterKey> = new Set([
  'view',
  'due',
  'sequence',
  'owner',
  'priority',
  'type',
  'state',
  'outcome',
]);

/**
 * Whether a filter change drops the row selection. Choosing a view, a due
 * window, a sequence, an owner or a priority does; typing in the search box or
 * a date field does not, so a keystroke never wipes what the rep ticked.
 */
export function filterChangeClearsSelection(
  update: Partial<Record<TaskFilterKey, unknown>>
): boolean {
  return (Object.keys(update) as TaskFilterKey[]).some((key) =>
    SELECTION_CLEARING.has(key)
  );
}

/** Which of my open tasks a view shows (Team and Completed are separate queries). */
export function inView(task: EngageTask, view: TaskView): boolean {
  if (view === 'calls') return task.type === 'Call';
  if (view === 'replies') return task.type === 'Reply';
  return true;
}

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The key the lead directory knows a task's lead by: the lead id when it is
 *  a real lead record, else their lowercased email. Null when neither. */
export function directoryPersonKey(task: {
  lead_id?: string | null;
  email?: string | null;
}): string | null {
  if (task.lead_id && UUID_PATTERN.test(task.lead_id)) return task.lead_id;
  const email = task.email?.trim().toLowerCase();
  return email || null;
}

type DatePart = { year: number | null; month: number | null } | null;

export { tenureLabel, roleTenure } from '@/lib/tenure';

/** Current roles first (newest start first), then past roles by when they
 *  ended. Providers return them in no dependable order. */
export function orderRoles<
  Role extends {
    starts_at: DatePart;
    ends_at: DatePart;
    is_current?: boolean | null;
    is_default?: boolean | null;
  },
>(roles: Role[]): Role[] {
  const stamp = (part: DatePart) =>
    part?.year ? part.year * 12 + (part.month ?? 1) : 0;
  return [...roles].sort((a, b) => {
    const aCurrent = isCurrentExperience(a);
    const bCurrent = isCurrentExperience(b);
    if (aCurrent !== bCurrent) return aCurrent ? -1 : 1;
    if (aCurrent && a.is_default !== b.is_default) return a.is_default ? -1 : 1;
    return aCurrent
      ? stamp(b.starts_at) - stamp(a.starts_at)
      : stamp(b.ends_at) - stamp(a.ends_at) ||
          stamp(b.starts_at) - stamp(a.starts_at);
  });
}

export type LocalTime = {
  /** "12:42 PM", with the weekday in front when it is not the rep's day. */
  label: string;
  /** Monday to Friday, 9 to 5, where the lead is. */
  working: boolean;
};

/** The lead's clock. Null for a zone the browser does not know. */
export function localTime(
  timezone: string | null | undefined,
  now: Date = new Date()
): LocalTime | null {
  if (!timezone) return null;
  try {
    const read = (zone: string | undefined) =>
      Object.fromEntries(
        new Intl.DateTimeFormat('en-US', {
          timeZone: zone,
          weekday: 'short',
          hour: 'numeric',
          minute: '2-digit',
          hourCycle: 'h23',
        })
          .formatToParts(now)
          .map((part) => [part.type, part.value])
      );
    const theirs = read(timezone);
    const mine = read(undefined);
    const hour = Number(theirs.hour);
    const time = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: 'numeric',
      minute: '2-digit',
    }).format(now);
    return {
      label:
        theirs.weekday === mine.weekday ? time : theirs.weekday + ' ' + time,
      working:
        !['Sat', 'Sun'].includes(theirs.weekday) && hour >= 9 && hour < 17,
    };
  } catch {
    return null;
  }
}

/** What a column sorts by. Empty values settle at the bottom. */
export function taskSortValue(
  task: EngageTask,
  column: string,
  today: string
): string | number | null {
  switch (column) {
    case 'task':
      return task.name.toLowerCase();
    case 'due':
      return `${task.due_date ?? today} ${task.time24 ?? '00:00'}`;
    case 'lead':
      return (task.person || task.email).toLowerCase();
    case 'title':
      return task.title?.toLowerCase() || null;
    case 'company':
      return task.company.toLowerCase() || null;
    case 'phone':
      return task.phones[0]?.number ?? null;
    case 'fit':
      return task.fit?.score ?? null;
    case 'script':
      return task.instructions?.toLowerCase() || null;
    case 'source':
      return (
        [taskSource(task), task.sequence_name].join(' ').toLowerCase() || null
      );
    case 'priority':
      return PRIORITY_RANK[task.priority] ?? 1;
    case 'owner':
      return task.assignee_user_id;
    case 'status':
      return task.status === 'completed'
        ? task.outcome || 'Done'
        : taskBlocked(task) || 'Ready';
    case 'email':
      return task.email.toLowerCase() || null;
    case 'location':
      return task.location?.toLowerCase() || null;
    case 'origin':
      return task.origin;
    case 'created':
      return task.created_at ? Date.parse(task.created_at) : null;
    case 'completed':
      return task.completed_at ? Date.parse(task.completed_at) : null;
    case 'call_length':
      return task.call_duration_seconds;
    case 'rank':
      return task.rank ?? null;
    case 'signal':
      return signalTier(task.signals);
    default:
      return null;
  }
}

// [size, suffix, fraction digits], largest first.
const COMPACT_USD_UNITS: [number, string, number][] = [
  [1e12, 'T', 2],
  [1e9, 'B', 2],
  [1e6, 'M', 1],
  [1e3, 'K', 1],
];

/** 1352600000 -> "$1.35B", 735000000 -> "$735M", 85000 -> "$85K". */
export function compactUsd(value: number | null | undefined): string | null {
  if (value == null || !Number.isFinite(value) || value <= 0) return null;
  // The unit is picked here and only the number goes through Intl: compact
  // currency formatting pads "$735.0M" on older engines (Node 22 in CI), plain
  // decimal formatting never does. A unit is used once the value rounds to at
  // least 1 of it, so 999,950 reads "$1M", not "$1,000K".
  const unit = COMPACT_USD_UNITS.find(
    ([size, , digits]) => Number((value / size).toFixed(digits)) >= 1
  );
  if (!unit) return '$' + Math.round(value).toLocaleString('en-US');
  const [size, suffix, digits] = unit;
  return (
    '$' +
    (value / size).toLocaleString('en-US', { maximumFractionDigits: digits }) +
    suffix
  );
}

/** 12.53 -> "+12.5%", -3 -> "-3%", 0 -> "0%". */
export function growthLabel(percent: number | null | undefined): string | null {
  if (percent == null || !Number.isFinite(percent)) return null;
  const rounded = Math.round(percent * 10) / 10;
  return (rounded > 0 ? '+' : '') + rounded + '%';
}

/** "series_b" -> "Series B", "secondary_market" -> "Secondary market". */
export function roundLabel(type: string | null | undefined): string | null {
  const text = (type ?? '').replace(/_/g, ' ').trim();
  if (!text) return null;
  return text
    .replace(/^./, (first) => first.toUpperCase())
    .replace(
      /\bseries ([a-z])\b/i,
      (_, letter: string) => 'Series ' + letter.toUpperCase()
    );
}

/** "2022-07-01T00:00:00" -> "Jul 2022"; anything unreadable -> null. */
export function monthYear(value: string | null | undefined): string | null {
  const match = (value ?? '').match(/^(\d{4})-(\d{2})/);
  if (!match) return null;
  const month = Number(match[2]);
  if (month < 1 || month > 12) return null;
  const names = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return names[month - 1] + ' ' + match[1];
}

/** A member's display name, falling back to the email. */
export function memberName(member: ShareableUser): string {
  return (
    [member.first_name, member.last_name].filter(Boolean).join(' ') ||
    member.email ||
    'Member'
  );
}

/** Who may hand a task to someone else: the same rule the server applies. */
export function canReassignTask(
  task: EngageTask,
  currentUserId: string | undefined,
  isAdmin: boolean
): boolean {
  if (task.status === 'completed') return false;
  return (
    isAdmin || !task.assignee_user_id || task.assignee_user_id === currentUserId
  );
}

/** The API takes at most this many task ids in one request. */
const TASK_IDS_PER_REQUEST = 200;

/** Task ids in request-sized batches, so a big selection still goes through. */
export function taskIdBatches(ids: string[]): string[][] {
  const batches: string[][] = [];
  for (let start = 0; start < ids.length; start += TASK_IDS_PER_REQUEST)
    batches.push(ids.slice(start, start + TASK_IDS_PER_REQUEST));
  return batches;
}

/** The due dates a queue is usually moved to, from today. */
export function dueDateChoices(
  today: Date
): { label: string; value: string }[] {
  const day = (date: Date) => format(date, 'yyyy-MM-dd');
  return [
    { label: 'Today', value: day(today) },
    { label: 'Tomorrow', value: day(addDays(today, 1)) },
    { label: 'Next Monday', value: day(nextMonday(today)) },
    { label: 'In a week', value: day(addDays(today, 7)) },
  ];
}

export const ORIGIN_LABEL: Record<EngageTask['origin'], string> = {
  step: 'Sequence step',
  reply: 'Reply',
  follow_up: 'Follow-up',
};

/** "Sep 18" for a day or instant the server gave us; nothing for nothing. */
export function dayLabel(value: string | null | undefined): string | null {
  if (!value) return null;
  const parsed = new Date(value.length === 10 ? value + 'T12:00:00' : value);
  return Number.isNaN(parsed.getTime()) ? null : format(parsed, 'MMM d');
}

export type CompletedFilter = 'any' | 'today' | 'week' | 'month' | 'custom';

export const COMPLETED_OPTIONS: readonly (readonly [
  CompletedFilter,
  string,
])[] = [
  ['any', 'Any time'],
  ['today', 'Today'],
  ['week', 'Last 7 days'],
  ['month', 'Last 30 days'],
  ['custom', 'Custom'],
];

/**
 * The completion window a filter stands for, as YYYY-MM-DD bounds for the
 * server, inclusive at both ends. Nothing for "any time" or an incomplete
 * custom range.
 */
export function completedWindow(
  filter: CompletedFilter,
  today: string,
  custom: { from: string; to: string }
): { from?: string; to?: string } {
  const day = (offset: number) =>
    format(addDays(new Date(today + 'T12:00:00'), offset), 'yyyy-MM-dd');
  switch (filter) {
    case 'today':
      return { from: today, to: today };
    case 'week':
      return { from: day(-6), to: today };
    case 'month':
      return { from: day(-29), to: today };
    case 'custom':
      return {
        from: custom.from || undefined,
        to: custom.to || undefined,
      };
    default:
      return {};
  }
}

/** Every facet the tasks page filters on, open work and finished alike. */
export type Filters = {
  view: TaskView;
  due: DueFilter;
  sequence: string;
  /** null = everyone. Applies to the Team and Completed views. */
  owner: string | null;
  priority: 'all' | EngageTaskPriority;
  /** Call, Task (manual) or Reply. */
  type: 'all' | 'Call' | 'Manual' | 'Reply';
  /** Ready to work, or blocked by its sequence or lead. */
  state: 'all' | 'ready' | 'blocked';
  /** Completed view only: the recorded call status. */
  outcome: string;
  /** Completed view only: when the work was finished. */
  completed: CompletedFilter;
  query: string;
  from: string;
  to: string;
};

/** How many open filters are set, so the page can offer to clear them. */
export function activeFilterCount(
  filters: Filters,
  completedView: boolean,
  ownerFiltered: boolean
): number {
  // The default due filter is where the page starts, not something "applied".
  return (
    Number(!completedView && filters.due !== 'due_now') +
    Number(filters.sequence !== 'all') +
    Number(ownerFiltered && filters.owner !== null) +
    Number(filters.priority !== 'all') +
    Number(
      filters.view !== 'calls' &&
        filters.view !== 'replies' &&
        filters.type !== 'all'
    ) +
    Number(!completedView && filters.state !== 'all') +
    Number(completedView && filters.outcome !== 'all') +
    Number(completedView && filters.completed !== 'any')
  );
}

/** The modifier this device's keyboard shortcuts use. */
export const MOD_KEY =
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad/i.test(navigator.platform)
    ? '⌘'
    : 'Ctrl';

type MarkdownNode = { type: string; value?: string; children?: MarkdownNode[] };

/**
 * A remark plugin that turns a single newline inside a paragraph into a line
 * break. Agents put one question or objection per line, and plain markdown
 * folds those lines into one paragraph. Only text nodes change, so code
 * blocks, inline code and list markup stay exactly as the parser read them.
 */
export function remarkLineBreaks() {
  const walk = (node: MarkdownNode) => {
    if (!node.children) return;
    node.children = node.children.flatMap((child): MarkdownNode[] => {
      if (child.type !== 'text' || !child.value?.includes('\n')) {
        walk(child);
        return [child];
      }
      return child.value
        .split(/\r?\n/)
        .flatMap((value, index) =>
          index
            ? [{ type: 'break' }, { type: 'text', value }]
            : [{ type: 'text', value }]
        );
    });
  };
  return walk;
}
